import { Injectable, Inject, PLATFORM_ID, TransferState, makeStateKey } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformServer } from '@angular/common';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { TenantService } from '../tenant/tenant';
import { API_BASE_URL } from '../config/api-config';
import { environment } from '../../../environments/environment';
import { IDocCase } from '../models/documentCase.modal';

const DOC_CASES_KEY = makeStateKey<IDocCase[]>('doc-cases');

@Injectable({
    providedIn: 'root'
})
export class DocCasesService {
    public docCases: IDocCase[] = [];

    constructor(
        private http: HttpClient,
        private transferState: TransferState,
        @Inject(PLATFORM_ID) private platformId: Object,
        private tenantService: TenantService,
        @Inject(API_BASE_URL) private apiBaseUrl: string
    ) { }

    async loadDocCases(): Promise<void> {
        let domain = this.tenantService.getDomain();

        if (!domain || domain === 'localhost' || domain === '127.0.0.1' || domain.includes('efrx')) {
            domain = environment.fallbackDomain;
        }

        // 1. Browser: reuse server-rendered data from TransferState
        if (this.transferState.hasKey(DOC_CASES_KEY)) {
            this.docCases = this.transferState.get(DOC_CASES_KEY, []);
            this.transferState.remove(DOC_CASES_KEY);
            return;
        }

        // 2. Fetch via internal proxy (runs on server during SSR, and on browser as fallback)
        try {
            const response = await firstValueFrom(
                this.http.get<IDocCase>(
                    `${this.apiBaseUrl}/internal-api/${domain}/doc-cases/all`
                ).pipe(
                    timeout(5000),
                    catchError((err) => {
                        console.error('[DocCases] HTTP error:', err);
                        return of({ data: [] } as unknown as IDocCase);
                    })
                )
            );

            this.docCases = response?.data || [];

            // 3. Save to TransferState on the server so the browser can reuse it
            if (isPlatformServer(this.platformId)) {
                this.transferState.set(DOC_CASES_KEY, this.docCases);
            }
        } catch (error) {
            console.error(`[DocCases] Failed to load for domain: ${domain}`, error);
            this.docCases = [];
        }
    }
}
